<template>
  <div class="deck-picker">
    <header class="page-header">
      <div>
        <p class="kicker">{{ t('deck.title') }}</p>
        <h1>{{ t('deck.savedDecks') }}</h1>
      </div>
      <div class="header-actions">
        <router-link to="/deck-builder/recycle" class="recycle-entry">
          <svg class="recycle-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 17l-2 2 2 2M10 19h9a2 2 0 0 0 1.75-2.75l-.55-1M8.536 11l-.732-2.732-2.732.732M7.804 8.268l-4.5 7.794a2 2 0 0 0 1.506 2.89l1.141.024M15.464 11l2.732.732.732-2.732M18.196 11.732l-4.5-7.794a2 2 0 0 0-3.256-.14l-.591.976"
            />
          </svg>
          {{ t('deck.recycle') }}
        </router-link>
        <router-link to="/" class="text-btn">
          {{ t('deck.backHome') }}
        </router-link>
      </div>
    </header>

    <p
      v-if="feedback"
      class="picker-feedback"
      :class="feedback.kind"
      role="status"
    >
      {{ feedback.text }}
    </p>

    <div class="deck-grid">
      <article
        v-for="slot in deckStore.decks"
        :key="slot.id"
        class="deck-card"
        :class="{ active: slot.id === deckStore.activeId }"
        :data-deck-id="slot.id"
      >
        <button
          type="button"
          class="fan-hit"
          :aria-label="tournamentDeckLocked(slot.id) ? t('deck.tournamentLock') : t('deck.openDeck')"
          @click="openDeck(slot.id)"
        >
          <div class="fan" aria-hidden="true">
            <span
              v-for="(card, index) in previewOf(slot)"
              :key="`${slot.id}-${index}`"
              class="fan-slot"
              :style="fanStyle(index)"
            >
              <CardArt
                v-if="card"
                :card-id="card.id"
                :foil="card.foil"
                :alt="card.name"
                intensity="thumb"
              />
              <CardArt v-else face-down alt="" intensity="thumb" />
            </span>
          </div>
          <span class="fan-cta">
            {{ tournamentDeckLocked(slot.id) ? t('deck.tournamentLock') : t('deck.openDeck') }}
          </span>
        </button>

        <div class="meta">
          <label class="name-field">
            <span class="visually-hidden">{{ t('deck.deckName') }}</span>
            <input
              class="md-field"
              :value="slot.name"
              maxlength="48"
              :readonly="tournamentDeckLocked(slot.id)"
              :aria-label="t('deck.deckName')"
              @click.stop
              @keydown.enter.prevent="blurTarget($event)"
              @blur="commitName(slot.id, $event)"
            >
          </label>
          <p class="counts">
            {{ slot.mainDeckCards.length }}/40
            · Extra {{ slot.extraDeckCards.length }}/15
          </p>
          <span
            v-if="slot.id === deckStore.activeId"
            class="badge"
          >
            {{ t('deck.inDuels') }}
          </span>
          <span
            v-if="tournamentDeckLocked(slot.id)"
            class="badge"
          >
            {{ t('deck.tournamentLock') }}
          </span>
          <div class="actions">
            <button
              type="button"
              class="md-btn-text"
              :disabled="slot.id === deckStore.activeId"
              @click="onUseInDuels(slot.id)"
            >
              {{ t('deck.useInDuels') }}
            </button>
            <button
              type="button"
              class="md-btn-text"
              :disabled="!deckStore.canCreate"
              @click="onDuplicate(slot.id)"
            >
              {{ t('deck.duplicate') }}
            </button>
            <button
              type="button"
              class="md-btn-text danger"
              :disabled="tournamentDeckLocked(slot.id)"
              @click="onDelete(slot.id)"
            >
              {{ t('deck.deleteDeck') }}
            </button>
          </div>
        </div>
      </article>

      <button
        type="button"
        class="deck-card add-card"
        :disabled="!deckStore.canCreate"
        :title="deckStore.canCreate ? undefined : t('deck.deckLimit')"
        @click="onCreate"
      >
        <span class="add-plus" aria-hidden="true">+</span>
        <span class="add-label">{{ t('deck.newDeckCard') }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import CardArt from '../components/CardArt.vue'
import { topFoilPreview } from '../game/deckPreview'
import {
  activateFirstPlayable,
  getSlotDeckStatus,
} from '../game/localDeck'
import { useI18n } from '../i18n/t'
import { useDeckStore, tournamentDeckLocked, type DeckSlot } from '../stores/deck'

const FAN_TILTS = [-18, -9, 0, 9, 18]

const { t } = useI18n()
const router = useRouter()
const deckStore = useDeckStore()
deckStore.ensureIntegrity()

const feedback = ref<{ kind: 'ok' | 'warn'; text: string } | null>(null)
let feedbackTimer: ReturnType<typeof setTimeout> | null = null

function previewOf(slot: DeckSlot) {
  return topFoilPreview(slot)
}

function fanStyle(index: number): Record<string, string> {
  const tilt = FAN_TILTS[index] ?? 0
  const z = 3 - Math.abs(index - 2)
  return {
    '--tilt': `${tilt}deg`,
    zIndex: String(z),
  }
}

function openDeck(id: string): void {
  if (tournamentDeckLocked(id)) {
    showFeedback('warn', t('deck.tournamentLocked'))
    return
  }
  deckStore.setEditingId(id)
  void router.push({ name: 'deck-editor', params: { deckId: id } })
}

function blurTarget(event: Event): void {
  ;(event.target as HTMLInputElement).blur()
}

function commitName(id: string, event: Event): void {
  const el = event.target as HTMLInputElement
  if (tournamentDeckLocked(id)) {
    const slot = deckStore.decks.find((row) => row.id === id)
    el.value = slot?.name ?? el.value
    return
  }
  deckStore.renameDeck(id, el.value)
  const slot = deckStore.decks.find((row) => row.id === id)
  el.value = slot?.name ?? el.value
}

function showFeedback(kind: 'ok' | 'warn', text: string): void {
  feedback.value = { kind, text }
  if (feedbackTimer) clearTimeout(feedbackTimer)
  feedbackTimer = setTimeout(() => {
    feedback.value = null
  }, 8000)
}

async function onCreate(): Promise<void> {
  if (!deckStore.canCreate) {
    showFeedback('warn', t('deck.deckLimit'))
    return
  }
  const id = deckStore.createDeck(t('deck.defaultName'))
  if (!id) {
    showFeedback('warn', t('deck.deckLimit'))
    return
  }
  await nextTick()
  const input = document.querySelector(
    `[data-deck-id="${id}"] input`,
  ) as HTMLInputElement | null
  input?.focus()
  input?.select()
}

function onDuplicate(id: string): void {
  if (!deckStore.canCreate) {
    showFeedback('warn', t('deck.deckLimit'))
    return
  }
  const src = deckStore.decks.find((slot) => slot.id === id)
  const name = t('deck.copySuffix', {
    name: src?.name || t('deck.myDeck'),
  })
  const next = deckStore.duplicateDeck(id, name)
  if (!next) showFeedback('warn', t('deck.deckLimit'))
}

function onDelete(id: string): void {
  if (tournamentDeckLocked(id)) {
    showFeedback('warn', t('deck.tournamentLocked'))
    return
  }
  const src = deckStore.decks.find((slot) => slot.id === id)
  const name = src?.name || t('deck.myDeck')
  if (!window.confirm(t('deck.confirmDelete', { name }))) return
  deckStore.deleteDeck(id)
  activateFirstPlayable()
}

function onUseInDuels(id: string): void {
  const slot = deckStore.decks.find((row) => row.id === id)
  if (getSlotDeckStatus(slot).reason !== 'ok') {
    showFeedback('warn', t('deck.useNeedLegal'))
    return
  }
  deckStore.setActiveId(id)
  const name = slot?.name || t('deck.myDeck')
  showFeedback('ok', t('deck.savedOkActive', { name }))
}

onUnmounted(() => {
  if (feedbackTimer) clearTimeout(feedbackTimer)
})
</script>

<style scoped>
.deck-picker {
  width: min(1100px, 100%);
  margin: 0 auto;
  padding: 24px 16px 48px;
  color: var(--md-sys-color-on-surface);
  font-family: Roboto, system-ui, sans-serif;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

.kicker {
  margin: 0 0 8px;
  color: var(--md-sys-color-primary);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

h1 {
  margin: 0;
  font-size: 36px;
  line-height: 44px;
  font-weight: 400;
  white-space: nowrap;
}

.header-actions {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 12px;
}

.recycle-entry {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 16px 0 12px;
  border-radius: 20px;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.1px;
  text-decoration: none;
  white-space: nowrap;
}

.recycle-entry:hover {
  filter: brightness(1.08);
}

.recycle-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.text-btn {
  color: var(--md-sys-color-primary);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.1px;
  text-decoration: none;
  white-space: nowrap;
}

.text-btn:hover {
  color: var(--md-sys-color-on-surface);
}

.picker-feedback {
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 20px;
}

.picker-feedback.ok {
  color: var(--md-sys-color-primary);
}

.picker-feedback.warn {
  color: var(--md-sys-color-error, #ba1a1a);
}

.deck-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

.deck-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 0;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container-high);
  box-shadow: var(--md-elevation-1);
  color: inherit;
  text-align: left;
  font: inherit;
}

.deck-card.active {
  border-color: var(--md-sys-color-primary);
}

.fan-hit {
  display: block;
  width: 100%;
  padding: 28px 12px 12px;
  border: 0;
  background: var(--md-sys-color-surface-container);
  cursor: pointer;
  color: inherit;
}

.fan-hit:hover,
.fan-hit:focus-visible {
  outline: none;
  background: color-mix(
    in srgb,
    var(--md-sys-color-primary) 12%,
    var(--md-sys-color-surface-container)
  );
}

.fan {
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: 148px;
}

.fan-slot {
  position: relative;
  flex: 0 0 auto;
  width: 72px;
  aspect-ratio: 421 / 614;
  margin-left: -28px;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 8px 18px rgb(0 0 0 / 45%);
  transform: rotate(var(--tilt, 0deg));
  pointer-events: none;
}

.fan-slot :deep(.card-art) {
  position: absolute;
  inset: 0;
}

.fan-slot:first-child {
  margin-left: 0;
}

.fan-cta {
  display: block;
  margin-top: 10px;
  color: var(--md-sys-color-primary);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.3px;
}

.meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 16px 14px;
}

.name-field {
  display: block;
}

.md-field {
  width: 100%;
  min-height: 40px;
  padding: 8px 12px;
  border: 0;
  border-bottom: 1px solid var(--md-sys-color-outline);
  border-radius: 4px 4px 0 0;
  background: var(--md-sys-color-surface-container);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 16px;
}

.md-field:focus {
  outline: none;
  border-bottom-width: 2px;
  border-bottom-color: var(--md-sys-color-primary);
}

.md-field[readonly] {
  color: var(--md-sys-color-on-surface-variant);
}

.counts {
  margin: 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 13px;
}

.badge {
  align-self: flex-start;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--md-sys-color-primary);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.md-btn-text {
  padding: 4px 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--md-sys-color-primary);
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.md-btn-text:hover:not(:disabled) {
  background: var(--md-state-layer);
}

.md-btn-text.danger {
  color: var(--md-sys-color-error, #ba1a1a);
}

.md-btn-text:disabled {
  opacity: 0.38;
  cursor: not-allowed;
}

.add-card {
  min-height: 280px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  border: 1px dashed var(--md-sys-color-outline);
  background: var(--md-sys-color-surface-container);
  box-shadow: none;
}

.add-card:hover:not(:disabled),
.add-card:focus-visible:not(:disabled) {
  border-color: var(--md-sys-color-primary);
  background: color-mix(
    in srgb,
    var(--md-sys-color-primary) 10%,
    var(--md-sys-color-surface-container)
  );
}

.add-card:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.add-plus {
  font-size: 48px;
  line-height: 1;
  color: var(--md-sys-color-primary);
}

.add-label {
  font-size: 16px;
  font-weight: 500;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

@media (max-width: 640px) {
  .page-header {
    flex-direction: column;
  }

  h1 {
    white-space: normal;
  }

  .header-actions {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }

  .recycle-entry {
    width: 100%;
    min-height: 44px;
  }

  .header-actions .text-btn {
    align-self: flex-start;
    min-height: 44px;
    line-height: 44px;
  }

  .fan {
    height: 128px;
  }

  .fan-slot {
    width: 60px;
    margin-left: -22px;
  }

  .md-btn-text {
    min-height: 44px;
  }
}

@media (max-height: 500px) and (max-width: 1100px) {
  .deck-picker {
    padding: 12px 16px 28px;
  }

  .page-header {
    margin-bottom: 12px;
  }

  h1 {
    font-size: 22px;
    line-height: 28px;
  }

  .deck-grid {
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 10px;
  }

  .deck-card {
    flex-direction: row;
    align-items: stretch;
  }

  .fan-hit {
    width: 168px;
    flex-shrink: 0;
    padding: 12px 8px;
  }

  .fan {
    height: 88px;
  }

  .fan-slot {
    width: 44px;
    margin-left: -16px;
  }

  .meta {
    padding: 8px 12px 10px;
    justify-content: center;
  }

  .add-card {
    min-height: 88px;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .add-plus {
    font-size: 32px;
  }
}
</style>
